if (!customElements.get('raceit-newsletter-popup')) {
  // Phones: portrait below the theme's md breakpoint, plus phones held sideways
  // (wide but short, with a touch screen). Must match raceit-newsletter-popup.css.
  const PHONE_QUERY = '(max-width: 768.98px), (pointer: coarse) and (max-height: 540px)';
  // First appearance of the edge tab: never earlier than this, whatever the
  // section delay says. Once it has appeared it follows the shopper from page
  // to page straight away, until they subscribe or close the newsletter.
  const MOBILE_TAB_MIN_DELAY = 30;
  const TAB_ARMED_DAYS = 365;
  // Pause after the cookie banner is answered before anything else appears.
  const CONSENT_GRACE_MS = 8000;
  // First arrival only: the teaser slides out after a beat and stays a moment.
  const TEASER_DELAY_MS = 700;
  const TEASER_SHOW_MS = 3600;
  customElements.whenDefined('modal-dialog').then(() => {
    if (customElements.get('raceit-newsletter-popup')) return;
    class RaceitNewsletterPopup extends Modal {
      connectedCallback() {
        this.builder = this.dataset.builder === 'true';
        this.dismissKey = this.builder ? 'raceit-newsletter-builder-dismissed' : `${this.id}-dismissed`;
        this.requestKey = 'raceit-newsletter-requested';
        this.armedKey = `${this.dismissKey}-tab`;
        this.form = this.querySelector('form');
        this.success = this.querySelector('[data-newsletter-success]');
        this.fields = this.querySelector('[data-newsletter-fields]');
        this.dialog = this.querySelector('[role="dialog"]');
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.form?.addEventListener('submit', () => {
          const url = new URL(window.location.href);
          url.searchParams.set('customer_posted', 'true');
          url.searchParams.set('newsletter_requested', '1');
          this.form.querySelector('[name="return_to"]').value = `${url.pathname}${url.search}${url.hash}`;
          this.querySelector('[type="submit"]').disabled = true;
        }, options);
        window.addEventListener('pageshow', () => {
          const submit = this.querySelector('[type="submit"]');
          if (submit) submit.disabled = false;
        }, options);
        if (this.querySelector('[data-newsletter-error]')) {
          this.fields.hidden = false;
          this.success.hidden = true;
          this.open();
          return;
        }
        const params = new URL(window.location.href).searchParams;
        const returned = this.success?.dataset.formPosted === 'true'
          || (params.get('customer_posted') === 'true' && params.get('newsletter_requested') === '1');
        if (returned) {
          this.remember(this.requestKey, 30);
          this.fields.hidden = true;
          this.success.hidden = false;
          const url = new URL(window.location.href);
          url.searchParams.delete('newsletter_requested');
          url.searchParams.delete('customer_posted');
          window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
          this.open();
          return;
        }
        if (Shopify.designMode) {
          document.addEventListener('shopify:section:select', (event) => {
            if (event.target === this.closest('.shopify-section')) this.open();
          }, options);
          document.addEventListener('shopify:section:deselect', () => this.close(), options);
          return;
        }
        if (this.remembered(this.requestKey) || this.remembered(this.dismissKey)) return;
        if (window.matchMedia(PHONE_QUERY).matches) {
          // Phones: never a popup (Google intrusive interstitials, and it used to land on top of the
          // cookie banner). An R tab sits on the edge; the shopper opens the drawer from it.
          if (!this.builder && this.dataset.showOnMobile !== 'true') return;
          this.scheduleTab(options);
          return;
        }
        if (this.builder || this.dataset.trigger === 'delay') {
          this.timer = window.setTimeout(
            () => this.whenConsentClear(() => this.open(), options),
            this.builder ? 0 : Number(this.dataset.delay) * 1000
          );
        } else if (this.dataset.trigger === 'exit') {
          document.body.addEventListener('mouseleave', (event) => {
            if (event.clientY < 0) this.open();
          }, options);
        } else if (this.dataset.trigger === 'clipboard') {
          document.addEventListener('copy', () => this.open(), options);
        }
      }

      scheduleTab(options) {
        this.tab = document.querySelector(`[data-newsletter-tab="${this.id}"]`);
        if (!this.tab) return;
        this.tab.addEventListener('click', () => this.open(), options);
        // The cookie banner can be injected after the tab is already out: step
        // aside while it is open and come back once it is answered.
        document.addEventListener('rc:consent-change', (event) => {
          if (this.tab.hidden || this.hasAttribute('open')) return;
          this.tab.classList.toggle('is-visible', !event.detail.open);
        }, options);
        // First time: after the long delay. Afterwards it is already there on every page.
        const armed = this.remembered(this.armedKey);
        const delay = armed ? 0 : Math.max(Number(this.dataset.delay) || 0, MOBILE_TAB_MIN_DELAY) * 1000;
        this.timer = window.setTimeout(() => this.whenConsentClear(() => this.showTab(!armed), options), delay);
      }

      showTab(firstTime) {
        if (!this.tab || this.remembered(this.requestKey) || this.remembered(this.dismissKey)) return;
        this.remember(this.armedKey, TAB_ARMED_DAYS);
        const tab = this.tab;
        tab.hidden = false;
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
          if (!window.raceitConsent?.isOpen()) tab.classList.add('is-visible');
        }));
        this.tabTimers = [];
        const later = (fn, ms) => this.tabTimers.push(window.setTimeout(fn, ms));
        if (firstTime && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          // First arrival: the teaser slides out next to the R, then tucks back in.
          later(() => tab.classList.add('is-teasing'), TEASER_DELAY_MS);
          later(() => tab.classList.remove('is-teasing'), TEASER_DELAY_MS + TEASER_SHOW_MS);
          later(() => tab.classList.add('is-alive'), TEASER_DELAY_MS + TEASER_SHOW_MS + 400);
        } else {
          // The R keeps a steady shake (CSS loop) so it always reads as tappable.
          tab.classList.add('is-alive');
        }
      }

      stopTabMotion() {
        (this.tabTimers || []).forEach((id) => window.clearTimeout(id));
        this.tabTimers = [];
        this.tab?.classList.remove('is-teasing', 'is-alive');
      }

      // Runs fn now, or once the cookie banner has been answered (plus a pause), so the two never
      // compete for the same screen. mobile-cro.js publishes the banner state.
      whenConsentClear(fn, options) {
        const consent = window.raceitConsent;
        if (!consent || !consent.isOpen()) {
          fn();
          return;
        }
        const onChange = () => {
          if (consent.isOpen()) return;
          document.removeEventListener('rc:consent-change', onChange);
          this.timer = window.setTimeout(fn, CONSENT_GRACE_MS);
        };
        document.addEventListener('rc:consent-change', onChange, { signal: options.signal });
      }

      remembered(key) {
        return document.cookie.split('; ').some((cookie) => cookie.startsWith(`${encodeURIComponent(key)}=`));
      }

      remember(key, days) {
        if (Shopify.designMode) return;
        document.cookie = `${encodeURIComponent(key)}=1; Max-Age=${Math.round(days * 86400)}; Path=/; SameSite=Lax; Secure`;
      }

      open() {
        if (this.hasAttribute('open')) return;
        if (!Shopify.designMode && this.success.hidden && !this.querySelector('[data-newsletter-error]')
          && (this.remembered(this.dismissKey) || this.remembered(this.requestKey))) return;
        if (document.querySelector('.modal[open], .drawer[open]')) return;
        this.priorFocus = document.activeElement;
        if (this.tab) {
          this.stopTabMotion();
          this.tab.setAttribute('aria-expanded', 'true');
          this.tab.classList.remove('is-visible');
        }
        super.open(this.priorFocus);
        trapFocus(this.dialog, this.dialog);
        this.background = this.builder ? document.getElementById('main-content') : null;
        this.wasInert = this.background?.inert;
        if (this.background) this.background.inert = true;
      }

      handleClick(event) {
        if (event.target === this || event.target.closest('.js-close-modal')) this.close();
      }

      close() {
        if (!this.hasAttribute('open')) return;
        if (this.background) this.background.inert = this.wasInert;
        super.close();
        // Closing the newsletter (×, "keep browsing", tapping outside, Esc) is the
        // shopper's "no": the tab goes away and stays away.
        this.remember(this.dismissKey, this.builder ? 1 : Number(this.dataset.dismissDays) || 30);
        this.remember(this.armedKey, 0);
        window.clearTimeout(this.timer);
        if (this.tab) {
          this.tab.setAttribute('aria-expanded', 'false');
          this.tab.hidden = true;
        }
        if (this.builder && (!this.priorFocus || this.priorFocus === document.body)) {
          document.querySelector('[data-bike-studio] button')?.focus({ preventScroll: true });
        }
      }

      disconnectedCallback() {
        window.clearTimeout(this.timer);
        this.stopTabMotion();
        this.controller?.abort();
        if (this.hasAttribute('open')) this.close();
      }
    }
    customElements.define('raceit-newsletter-popup', RaceitNewsletterPopup);
  });
}
